import type { ApartadoMovement, ApartadoStatus, Occupancy, ReservationRequest, RequestStatus, RequestStatusGroup, TripContent, TripSeason } from "./types";

/** Spanish copy for the request status vocabulary (A6, state model). */
export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  submitted: "Solicitud enviada",
  in_review: "En revisión",
  proposal_ready: "Propuesta lista",
  services_confirmed: "Servicios garantizados",
  confirmed: "Confirmada",
  expired: "Vencida",
  cancelled: "Cancelada",
  unavailable: "No disponible"
};

export const REQUEST_STATUS_TONES: Record<RequestStatus, "review" | "proposal" | "confirmed" | "expired" | "cancelled"> = {
  submitted: "review", in_review: "review", proposal_ready: "proposal", services_confirmed: "confirmed", confirmed: "confirmed",
  expired: "expired", cancelled: "cancelled", unavailable: "cancelled"
};

export const GROUP_FILTERS: Array<{ id: RequestStatusGroup | "all"; label: string }> = [
  { id: "all", label: "Todas" },
  { id: "review", label: "En revisión" },
  { id: "proposal", label: "Propuesta lista" },
  { id: "confirmed", label: "Confirmadas" },
  { id: "closed", label: "Cerradas" }
];

export const OCCUPANCY_INFO: Record<Occupancy, { short: string; long: string; capacity: number }> = {
  single: { short: "SGL", long: "Individual", capacity: 1 },
  double: { short: "DBL", long: "Doble", capacity: 2 },
  triple: { short: "TPL", long: "Triple", capacity: 3 },
  quadruple: { short: "CPL", long: "Cuádruple", capacity: 4 },
  child: { short: "CHD", long: "Menor", capacity: 1 }
};

export const ROOM_TYPES: Occupancy[] = ["double", "triple", "single"];

export const APARTADO_STATUS_LABELS: Record<ApartadoStatus, string> = {
  funding_pending: "Fondeo en proceso",
  active: "Apartado activo",
  funding_failed: "Fondeo no completado",
  completed: "Fondos aplicados",
  closed: "Cerrado"
};

/** The Apartados hub's editorial card (client design "Apartados Redesign", 13 Oct 2026). */
export const APARTADO_PITCH = {
  eyebrow: "Apartados Aventuria",
  title: "Tu próximo viaje, a tu ritmo",
  cta: "Encuentra tu próximo viaje",
  steps: [
    { title: "Aparta hoy y empieza a hacer planes.", body: "Da el primer paso sin pagar todo de una vez." },
    { title: "Tú marcas el ritmo.", body: "Abona según tus posibilidades, sin mensualidades fijas." },
    { title: "Una fecha clara, un viaje por disfrutar.", body: "Completa el pago un mes antes de salir." }
  ]
} as const;

/** "$9,000" style amount for the per-passenger minimum (whole pesos, no currency suffix). */
export function perPassengerLabel(amount: number): string {
  return `$${whole.format(amount)}`;
}

/** The pitch body; the per-passenger minimum is the admin setting `apartadoMinPerPassengerMxn`. */
export function apartadoPitchBody(perPassenger: number): string {
  return `Aparta con solo ${perPassengerLabel(perPassenger)} MXN por pasajero y abona cuánto y cuándo prefieras. Completa el pago un mes antes de tu salida.`;
}

/** The "En proceso de confirmación" pill and the request-status line the Apartado shows. */
export function reservationStatusCopy(status: RequestStatus): { label: string; note: string } {
  switch (status) {
    case "submitted":
    case "in_review":
      return { label: "En proceso de confirmación", note: "Tu asesor revisa disponibilidad y servicios. Te escribe en menos de 24 horas hábiles." };
    case "proposal_ready":
      return { label: "Propuesta lista", note: "Tu asesor preparó una propuesta. Revísala en Mis solicitudes." };
    case "services_confirmed":
      return { label: "Servicios garantizados", note: "Los servicios quedaron garantizados; falta confirmar la reservación." };
    case "confirmed":
      return { label: "Reservación confirmada", note: "Tu viaje está confirmado. Tu asesor te compartirá los siguientes pasos." };
    case "expired":
      return { label: "Propuesta vencida", note: "La propuesta venció. Puedes duplicar la solicitud con otras fechas." };
    case "cancelled":
      return { label: "Solicitud cancelada", note: "La solicitud quedó cancelada." };
    case "unavailable":
      return { label: "Servicios no confirmados", note: "Tu asesor te propondrá alternativas." };
  }
}

/** Timeline steps (A6.2 / C4): submission, review, proposal, services, confirmation, plus a closing state. */
export function requestTimeline(request: ReservationRequest): Array<{ key: string; title: string; detail: string; state: "done" | "now" | "todo" | "bad" }> {
  const events = request.timeline;
  const at = (status: RequestStatus) => events.find((event) => event.toStatus === status)?.createdAt ?? null;
  const order: RequestStatus[] = ["submitted", "in_review", "proposal_ready", "services_confirmed", "confirmed"];
  const reached = order.indexOf(request.status);
  const closed = request.statusGroup === "closed";
  const format = (value: string | null) => (value ? new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(value)).replace(".", "") : null);
  const steps: Array<{ key: string; title: string; detail: string; state: "done" | "now" | "todo" | "bad" }> = [
    { key: "submitted", title: "Solicitud enviada", status: "submitted" as RequestStatus },
    { key: "in_review", title: "Revisión del asesor", status: "in_review" as RequestStatus },
    { key: "proposal_ready", title: request.kind === "personalized" ? "Propuesta enviada" : "Propuesta lista", status: "proposal_ready" as RequestStatus },
    { key: "services_confirmed", title: "Servicios garantizados", status: "services_confirmed" as RequestStatus },
    { key: "confirmed", title: "Reservación confirmada", status: "confirmed" as RequestStatus }
  ].map((step, index) => {
    const date = at(step.status);
    const done = closed ? Boolean(date) : index < reached || (index === reached && index === order.length - 1);
    const now = !closed && index === reached && index !== order.length - 1;
    const state: "done" | "now" | "todo" = done ? "done" : now ? "now" : "todo";
    const detail = date ? format(date)! : state === "now" ? (index === 1 ? "En proceso · menos de 24 h hábiles" : "En proceso") : "Pendiente";
    return { key: step.key, title: step.title, detail, state };
  });
  // Submission is done the moment the request exists, even before the advisor moves it.
  if (steps[0]) { steps[0].state = "done"; steps[0].detail = format(at("submitted") ?? request.submittedAt) ?? steps[0].detail; }
  if (steps[1] && request.status === "submitted") { steps[1].state = "now"; steps[1].detail = "En proceso · menos de 24 h hábiles"; }
  if (closed) {
    const closing = events.find((event) => event.toStatus === request.status);
    steps.push({ key: request.status, title: REQUEST_STATUS_LABELS[request.status], detail: format(closing?.createdAt ?? request.lastActivityAt) ?? "", state: "bad" });
  }
  return steps;
}

/** Suggested room distributions for `travelers` people (A3 "Offer suggested distributions"). */
export function roomSuggestions(travelers: number): Array<{ label: string; rooms: Occupancy[] }> {
  const out: Array<{ t: number; d: number; s: number }> = [];
  for (let t = 0; t <= Math.floor(travelers / 3); t += 1) {
    for (let d = 0; d <= Math.floor((travelers - 3 * t) / 2); d += 1) {
      const s = travelers - 3 * t - 2 * d;
      if (s < 0) continue;
      out.push({ t, d, s });
    }
  }
  out.sort((left, right) => (left.t + left.d + left.s) - (right.t + right.d + right.s) || left.s - right.s || right.d - left.d);
  return out.slice(0, 3).map((combo) => ({
    label: occupancyLabel([...Array<Occupancy>(combo.t).fill("triple"), ...Array<Occupancy>(combo.d).fill("double"), ...Array<Occupancy>(combo.s).fill("single")]),
    rooms: [...Array<Occupancy>(combo.t).fill("triple"), ...Array<Occupancy>(combo.d).fill("double"), ...Array<Occupancy>(combo.s).fill("single")]
  }));
}

export function autoRooms(travelers: number): Occupancy[] {
  const rooms: Occupancy[] = [];
  let left = travelers;
  while (left >= 2) { rooms.push(left === 3 ? "triple" : "double"); left -= left === 3 ? 3 : 2; }
  if (left === 1) rooms.push("single");
  return rooms;
}

export function occupancyLabel(rooms: Occupancy[]): string {
  const counts = new Map<Occupancy, number>();
  for (const room of rooms) counts.set(room, (counts.get(room) ?? 0) + 1);
  return (["triple", "double", "single", "quadruple"] as Occupancy[]).filter((room) => counts.has(room)).map((room) => {
    const count = counts.get(room)!;
    const name = OCCUPANCY_INFO[room].long.toLowerCase();
    return `${count} ${count === 1 ? name : `${name}s`}`;
  }).join(" + ") || "Sin asignar";
}

export function roomCapacity(rooms: Occupancy[]): number {
  return rooms.reduce((sum, room) => sum + OCCUPANCY_INFO[room].capacity, 0);
}

/** Whether a season row can price a departure: its own departure list when it has one, else its range. Mirrors the API. */
export function seasonContains(season: TripSeason, date: string): boolean {
  const listed = season.departure_dates ?? [];
  if (listed.length) return listed.includes(date);
  return (!season.valid_from || season.valid_from <= date) && (!season.valid_to || season.valid_to >= date);
}

/** The per-person date supplements due on a departure (add-ons that list it). Mirrors the API. */
export function supplementFor(content: TripContent, departureDate: string | null): number {
  if (!departureDate) return 0;
  return content.pricing.add_ons.reduce((sum, addOn) => sum + (addOn.amount !== null && (addOn.departure_dates ?? []).includes(departureDate) ? addOn.amount : 0), 0);
}

/**
 * The season that prices a departure date — of the chosen service category when there is one —
 * else the first, and its per-person rate for an occupancy, date supplement included
 * (`supplement`, already in `amount`). Mirrors the API.
 */
export function rateFor(content: TripContent, occupancy: Occupancy, departureDate: string | null, serviceCategory?: string | null): { amount: number; currency: string; season: TripSeason; supplement: number } | null {
  const seasons = content.pricing.seasons;
  const containing = departureDate ? seasons.filter((candidate) => seasonContains(candidate, departureDate)) : [];
  const listed = (candidate: TripSeason) => (candidate.departure_dates ?? []).length > 0;
  // The most specific row wins: one that lists the departure, then one whose dates bracket it
  // ("01 OCT – 31 MAR"), then an undated base row of the same card; within each, the picked category first.
  const ranked = [
    ...containing.filter(listed),
    ...containing.filter((candidate) => !listed(candidate) && candidate.valid_from && candidate.valid_to),
    ...containing.filter((candidate) => !listed(candidate) && (!candidate.valid_from || !candidate.valid_to))
  ];
  // A picked category that does not price this date ("Primera · Temporada Alta" on a low-season
  // departure) keeps its tier — the part before " · " — before falling back to the first row.
  const tier = (category: string) => category.split(" · ")[0];
  const season = (serviceCategory
    ? ranked.find((candidate) => candidate.service_category === serviceCategory) ?? ranked.find((candidate) => tier(candidate.service_category) === tier(serviceCategory))
    : undefined) ?? ranked[0] ?? seasons[0];
  if (!season) return null;
  const rate = season.rates.find((candidate) => candidate.occupancy === occupancy) ?? season.rates.find((candidate) => candidate.occupancy === "double");
  if (!rate || rate.amount === null) return null;
  const supplement = supplementFor(content, departureDate);
  return { amount: rate.amount + supplement, currency: rate.currency || content.pricing.currency || "USD", season, supplement };
}

/** Mirrors the API estimate: each room at its occupancy's per-person rate. */
export function estimateTotal(content: TripContent, rooms: Occupancy[], departureDate: string | null, serviceCategory?: string | null): { total: number; currency: string; perPerson: number } | null {
  let total = 0;
  let priced = 0;
  let currency = content.pricing.currency || "USD";
  for (const room of rooms) {
    const rate = rateFor(content, room, departureDate, serviceCategory);
    if (!rate) return null;
    total += rate.amount * OCCUPANCY_INFO[room].capacity;
    priced += OCCUPANCY_INFO[room].capacity;
    currency = rate.currency;
  }
  if (!priced) return null;
  return { total: Math.round(total * 100) / 100, currency, perPerson: Math.round((total / priced) * 100) / 100 };
}

const whole = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
const cents = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "$1,360 USD" / "$18,000.00 MXN". */
export function amountWithCurrency(amount: number, currency: string, decimals = false): string {
  return `$${(decimals ? cents : whole).format(amount)} ${currency}`;
}

export function mxn(amount: number): string {
  return `$${cents.format(amount)} MXN`;
}

/** Departures on or after `earliest` (ISO date; the API's lead-time cut-off), sorted. */
export function upcomingDepartures(content: TripContent, earliest: string | Date = new Date()): string[] {
  const from = typeof earliest === "string" ? earliest : earliest.toISOString().slice(0, 10);
  return [...content.availability.departure_dates].filter((date) => date >= from).sort();
}

const weekdayDate = new Intl.DateTimeFormat("es-MX", { weekday: "short", day: "numeric", month: "short" });
const weekdayDateYear = new Intl.DateTimeFormat("es-MX", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export function departureLabel(iso: string, withYear = false): string {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year!, (month ?? 1) - 1, day ?? 1);
  const text = (withYear ? weekdayDateYear : weekdayDate).format(date).replace(/\./g, "");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Days until a date; negative when past. */
export function daysFromToday(iso: string | null): number | null {
  if (!iso) return null;
  const [year, month, day] = iso.split("-").map(Number);
  const target = new Date(year!, (month ?? 1) - 1, day ?? 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/** Title and tone of an Apartado movement for the activity list (C5.3, customer view). */
export function movementCopy(movement: ApartadoMovement): { title: string; sub: string; tone: "in" | "out" | "back" | "info" } {
  switch (movement.kind) {
    case "contribution":
      return { title: movement.reason?.startsWith("Aportación inicial") ? "Aportación inicial desde Wallet Libre" : "Abono desde Wallet Libre", sub: "Wallet Libre → Apartado", tone: "in" };
    case "goal_change":
      return { title: movement.actorType === "admin" ? "Meta ajustada por Aventuria" : "Ajustaste tu meta", sub: movement.reason ?? `Meta de ${mxn(movement.previousGoal ?? 0)} a ${mxn(movement.newGoal ?? 0)}`, tone: "info" };
    case "disposal":
      return {
        title: movement.destination === "refund_to_wallet" ? "Devolución a Wallet Libre" : "Aplicación de fondos a servicios del viaje",
        sub: movement.reason ?? (movement.destination === "refund_to_wallet" ? "Aventuria" : "Aventuria · pago de servicios"),
        tone: movement.destination === "refund_to_wallet" ? "back" : "out"
      };
    case "reversal":
      return { title: "Movimiento revertido", sub: movement.reason ?? "Corrección de Aventuria", tone: "back" };
    case "adjustment":
      return { title: "Ajuste operativo", sub: movement.reason ?? "Aventuria", tone: "info" };
  }
}
